import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetCatalogItemsApi } from './use-get-catalog-items-api';

const endpoint = 'catalog/v1/restaurants';
export const patchCatalogItemEndpointIdentifier = endpoint;

interface BodyType {
  isActive: boolean;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  itemId: string;
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function usePatchCatalogItemApi(restaurantId: string, warehouseId: string, options?: Options): ReturnType {
  const { refetch: refetchCatalogItems } = useGetCatalogItemsApi(restaurantId, warehouseId, { enabled: false });
  const toast = useCustomToast();
  let fullEndpoint;

  const mutationFn = async ({ body, itemId }: TVariables) => {
    fullEndpoint = `${endpoint}/${restaurantId}/warehouses/${warehouseId}/items/${itemId}`;

    try {
      const { data } = await http.patch(fullEndpoint, body);

      // Refetch warehouse items
      refetchCatalogItems();

      toast({
        description: 'New item have been updated successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
